import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Building2, Wallet, Users, TrendingUp, Shield, MessageSquare, CheckCircle, ArrowRight, XCircle, Menu, X, Linkedin, Calendar, Clock, ChevronUp, ZoomIn } from 'lucide-react';
import Footer from '../components/Footer';
import WaitlistForm from '../components/WaitlistForm';
import WaitlistCTA, { WaitlistCTAGroup } from '../components/WaitlistCTA';
import ImageCarousel from '../components/ImageCarousel';
import { getHomepageArticles } from '../data/articles';

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [waitlistRole, setWaitlistRole] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle hash navigation - redirect /#pricing to /pricing, scroll for others
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const elementId = hash.replace('#', '');
      
      // Redirect /#pricing to /pricing page
      if (elementId === 'pricing') {
        navigate('/pricing', { replace: true });
        return;
      }
      
      // For other hashes (like #how-it-works), scroll to section
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Clean up URL without the hash (don't modify URL on scroll)
          window.history.replaceState(null, '', window.location.pathname);
        }
      }, 100);
    }
  }, [location, navigate]);

  // Handle scroll from pricing page or other navigation with query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scroll = params.get('scroll');
    const role = params.get('role');
    
    if (scroll === 'waitlist' && role) {
      setTimeout(() => {
        scrollToWaitlist(role);
      }, 100);
    }
  }, [location]);

  // Track scroll position for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToWaitlist = (role) => {
    setWaitlistRole(role);
    const waitlistSection = document.getElementById('waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
      // Dispatch custom event to set role in form
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('setWaitlistRole', { detail: role }));
      }, 300);
    }
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Fixed Navigation - Dark blue */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-700 bg-slate-800/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo - Home Button */}
            <a 
              href="/" 
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0 mr-4 sm:mr-6 lg:mr-10 cursor-pointer" 
              title="Go to Home"
            >
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="sm:hidden"
                style={{ height: '38px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden sm:block md:hidden"
                style={{ height: '46px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden md:block lg:hidden"
                style={{ height: '54px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden lg:block"
                style={{ height: '64px', width: 'auto' }}
              />
            </a>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              <a href="#how-it-works" className="text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium whitespace-nowrap">
                What to Expect
              </a>
              <a href="#pricing" className="text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium whitespace-nowrap">
                Pricing
              </a>
              <Link to="/blog" className="text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to="/about" className="text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium">
                About
              </Link>
              <Link to="/faq" className="text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium">
                FAQ
              </Link>
              <a 
                href="https://www.linkedin.com/company/seedsynch" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-slate-300 hover:text-blue-400 transition-colors text-sm"
              >
                <span className="hidden xl:inline">Follow SeedSynch on</span>
                <Linkedin size={18} />
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-slate-800 border-t border-slate-700 py-4 space-y-3">
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2">
                What to Expect
              </a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2">
                Pricing
              </a>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2">
                Blog
              </Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2">
                About
              </Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2">
                FAQ
              </Link>
              <a 
                href="https://www.linkedin.com/company/seedsynch" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                <Linkedin size={18} /> LinkedIn
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer for fixed nav */}
      <div className="h-16 lg:h-[72px]"></div>

      {/* Hero Section - Light */}
      <section className="bg-slate-100 py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6">
          {/* Centered headline spanning full width */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e3a5f] mb-12 lg:mb-16 leading-tight text-center">
            A smarter way for founders and investors to find the right match.
          </h1>
          
          {/* Two-column layout: Left-aligned text + Right CTAs - vertically centered */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
            {/* Left column: Supporting text - left aligned */}
            <div className="flex-1 lg:max-w-2xl">
              <p className="text-lg lg:text-xl text-[#334d6e] mb-5 leading-relaxed">
                Equity-based connections built around fit and risk-sharing — at the earliest stages.
              </p>
              <p className="text-base lg:text-lg text-[#4a6382] mb-4 leading-relaxed">
                Founders often struggle to reach investors with compatible expectations. Investors, in turn, spend significant time filtering opportunities before real fit becomes clear.
              </p>
              <p className="text-base lg:text-lg text-[#4a6382] leading-relaxed">
                SeedSynch reduces this friction in early-stage matching by improving signal and enabling connections around structure, intent, and long-term orientation — before conversations begin.
              </p>
            </div>
            
            {/* Right column: CTAs stacked vertically, centered with text */}
            <div className="flex flex-col gap-4 mt-10 lg:mt-0 lg:flex-shrink-0">
              <WaitlistCTA 
                variant="founder" 
                theme="light"
                size="lg"
                onClick={() => scrollToWaitlist('Founder')}
                testId="hero-founder-cta"
              />
              <WaitlistCTA 
                variant="investor" 
                theme="light"
                size="lg"
                onClick={() => scrollToWaitlist('Investor')}
                testId="hero-investor-cta"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Why SeedSynch?</h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">Alignment First</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Match based on what matters — industry, stage, structure, and values.</p>
            </Card>

            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Clock className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">Save Time</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Skip the cold outreach. Start conversations with context and mutual interest.</p>
            </Card>

            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">NDA Protection</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Share pitch decks and sensitive details only after NDA acceptance.</p>
            </Card>

            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">Direct Messaging</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Communicate directly within the platform. No middlemen.</p>
            </Card>

            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Users className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">Co-founder Search</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Find co-founders who match your skills and commitment level.</p>
            </Card>

            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Building2 className="text-blue-400" size={24} />
              </div>
              <h3 className="text-[#c7d4e4] font-semibold mb-2">Verified Investors</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed">Investors are reviewed and verified by our team before activation.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* What SeedSynch Is / Is Not - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-12">What SeedSynch Is — and Isn't</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* IS */}
            <Card className="bg-white border-slate-200 p-8">
              <h3 className="text-xl font-semibold text-blue-600 mb-6 flex items-center gap-2">
                <CheckCircle size={24} /> SeedSynch IS
              </h3>
              <ul className="space-y-4 text-[#4a6382] text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                  <span>A <strong className="text-[#1e3a5f]">matchmaking platform</strong> connecting founders and investors based on declared preferences</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                  <span>A <strong className="text-[#1e3a5f]">discovery layer</strong> that helps you find aligned partners earlier in the process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                  <span>A <strong className="text-[#1e3a5f]">time-saver</strong> — fewer cold emails, more relevant conversations</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                  <span>A <strong className="text-[#1e3a5f]">neutral facilitator</strong> — we introduce, we don't advise</span>
                </li>
              </ul>
            </Card>

            {/* IS NOT */}
            <Card className="bg-white border-slate-200 p-8">
              <h3 className="text-xl font-semibold text-[#334d6e] mb-6 flex items-center gap-2">
                <XCircle size={24} /> SeedSynch IS NOT
              </h3>
              <ul className="space-y-4 text-[#4a6382] text-sm">
                <li className="flex items-start gap-3">
                  <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <span>A funding platform, lender, or crowdfunding site</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <span>An investment arranger or deal broker</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <span>A provider of financial, legal, or compliance advice</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                  <span>A participant in capital transactions — we don't touch money</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works - Dark */}
      <section id="how-it-works" className="bg-slate-800 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">What to Expect</h2>
          <p className="text-[#c7d4e4] text-center mb-12 max-w-2xl mx-auto">Here's how SeedSynch will work once we launch — three steps to meaningful connections.</p>
          
          {/* Grid with harmonized equal height cards - increased height for better image visibility */}
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {/* Card 1: Build Your Profile - Single static image */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center flex flex-col h-[600px]">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                <span className="text-blue-400 font-bold text-lg">1</span>
              </div>
              <h3 className="text-lg font-semibold text-[#c7d4e4] mb-3 flex-shrink-0">Build Your Profile</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed mb-4 flex-shrink-0">You'll share your criteria, preferences, and what you're looking for. Founders will add venture details; investors will specify focus areas.</p>
              {/* Single static image - clickable to expand */}
              <div 
                className="mt-auto h-[320px] w-full overflow-hidden rounded-lg relative group cursor-pointer"
                onClick={() => setExpandedImage({
                  src: 'https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/aqh73rln_image.png',
                  alt: 'Alex T. investor profile showing Family-Office type, New York USA location, Investment Criteria with ticket size USD 1,000,000 - 5,000,000, preferred stages seed and series-a, preferred industries FinTech AI/ML SaaS, Investment Preferences and Investment Strategy sections'
                })}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/aqh73rln_image.png"
                  alt="Alex T. investor profile showing Family-Office type, New York USA location, Investment Criteria with ticket size USD 1,000,000 - 5,000,000, preferred stages seed and series-a, preferred industries FinTech AI/ML SaaS, Investment Preferences and Investment Strategy sections"
                  className="w-full h-full object-cover shadow-md rounded-lg"
                  style={{ objectPosition: '25% center' }}
                />
                {/* Zoom icon overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center rounded-lg">
                  <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                </div>
              </div>
            </Card>

            {/* Card 2: Discover Aligned Matches - Single static image */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center flex flex-col h-[600px]">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                <span className="text-blue-400 font-bold text-lg">2</span>
              </div>
              <h3 className="text-lg font-semibold text-[#c7d4e4] mb-3 flex-shrink-0">Discover Aligned Matches</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed mb-4 flex-shrink-0">Our matching system will surface profiles based on your declared preferences, showing alignment scores and key compatibility factors.</p>
              {/* Single static image - clickable to expand - center-center alignment */}
              <div 
                className="mt-auto h-[320px] w-full overflow-hidden rounded-lg relative group cursor-pointer"
                onClick={() => setExpandedImage({
                  src: 'https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/fz91nd2w_image.png',
                  alt: 'Discovery page showing three investor profile cards - Alex Thompson (64% match, Family-Office, New York USA), Fatima Khan (62.3% match, Angel, Doha Qatar), and Raj Patel (60% match, VC, Singapore Asia) with detailed compatibility breakdowns including Industry Fit, Stage Match, Funding Fit, Location scores and Investment Alignment criteria'
                })}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/fz91nd2w_image.png"
                  alt="Discovery page showing three investor profile cards - Alex Thompson (64% match, Family-Office, New York USA), Fatima Khan (62.3% match, Angel, Doha Qatar), and Raj Patel (60% match, VC, Singapore Asia) with detailed compatibility breakdowns including Industry Fit, Stage Match, Funding Fit, Location scores and Investment Alignment criteria"
                  className="w-full h-full object-cover shadow-md rounded-lg"
                  style={{ objectPosition: 'center center' }}
                />
                {/* Zoom icon overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center rounded-lg">
                  <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                </div>
              </div>
            </Card>

            {/* Card 3: Connect With Intent - USER PROVIDED IMAGE */}
            <Card className="bg-slate-700/50 border-slate-600 p-6 text-center flex flex-col h-[600px]">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                <span className="text-blue-400 font-bold text-lg">3</span>
              </div>
              <h3 className="text-lg font-semibold text-[#c7d4e4] mb-3 flex-shrink-0">Connect With Intent</h3>
              <p className="text-[#a3b8d1] text-sm leading-relaxed mb-4 flex-shrink-0">You'll be able to express interest, exchange messages, and share materials under NDA — starting conversations that matter.</p>
              {/* Single static image - clickable to expand */}
              <div 
                className="mt-auto h-[320px] w-full overflow-hidden rounded-lg relative group cursor-pointer"
                onClick={() => setExpandedImage({
                  src: 'https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/fga2idjt_image.png',
                  alt: 'Actions panel showing Express Interest with Matched status, Relationship Status section with Current Stage Matched and Next Milestone Initial Conversation, Recommended Next Action Request Initial Call button, You are Matched success message, Relationship Actions with Request Initial Call Propose Pitch Session and View Relationship Progress, Message/Chat and Save Profile options'
                })}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_01182398-f786-4c17-b380-b08e3e403335/artifacts/fga2idjt_image.png"
                  alt="Actions panel showing Express Interest with Matched status, Relationship Status section with Current Stage Matched and Next Milestone Initial Conversation, Recommended Next Action Request Initial Call button, You are Matched success message, Relationship Actions with Request Initial Call Propose Pitch Session and View Relationship Progress, Message/Chat and Save Profile options"
                  className="w-full h-full object-cover shadow-md rounded-lg"
                  style={{ objectPosition: '15% center' }}
                />
                {/* Zoom icon overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center rounded-lg">
                  <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Logic - Light */}
      <section id="pricing" className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-4">Simple, Transparent Pricing</h2>
          <p className="text-[#334d6e] text-center mb-12 max-w-2xl mx-auto leading-relaxed">
            Pay for access, not outcomes. We don't take commission, carry, or success fees. Your subscription is your only cost.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Founders */}
            <Card className="bg-white border-slate-200 p-6">
              <div className="text-center mb-6">
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-3">FOR FOUNDERS</div>
                <h3 className="text-lg font-semibold text-[#1e3a5f]">From Free</h3>
                <p className="text-[#334d6e] text-sm">Explore, then upgrade</p>
              </div>
              <ul className="space-y-3 text-sm text-[#4a6382] mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Profile creation</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Limited investor previews</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Co-founder search (paid)</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Direct messaging (paid)</li>
              </ul>
              <WaitlistCTA 
                variant="founder"
                theme="light"
                size="sm"
                onClick={() => scrollToWaitlist('founder')}
                testId="pricing-founder-cta"
                className="w-full"
              />
            </Card>

            {/* Investors */}
            <Card className="bg-white border-slate-200 p-6">
              <div className="text-center mb-6">
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-3">FOR INVESTORS</div>
                <h3 className="text-lg font-semibold text-[#1e3a5f]">From Free</h3>
                <p className="text-[#334d6e] text-sm">Browse, then unlock</p>
              </div>
              <ul className="space-y-3 text-sm text-[#4a6382] mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Venture browsing</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Save to watchlist</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> NDA pitch deck access (paid)</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Advanced filters (paid)</li>
              </ul>
              <WaitlistCTA 
                variant="investor"
                theme="light"
                size="sm"
                onClick={() => scrollToWaitlist('investor')}
                testId="pricing-investor-cta"
                className="w-full"
              />
            </Card>

            {/* Co-founders */}
            <Card className="bg-white border-slate-200 p-6">
              <div className="text-center mb-6">
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-3">FOR CO-FOUNDERS</div>
                <h3 className="text-lg font-semibold text-[#1e3a5f]">From Free</h3>
                <p className="text-[#334d6e] text-sm">Find your match</p>
              </div>
              <ul className="space-y-3 text-sm text-[#4a6382] mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Profile creation</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Limited matching</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Unlimited messaging (paid)</li>
                <li className="flex items-center gap-2"><CheckCircle className="text-blue-500" size={16} /> Visibility boost (paid)</li>
              </ul>
              <WaitlistCTA 
                variant="founder"
                theme="light"
                size="sm"
                onClick={() => scrollToWaitlist('founder')}
                testId="pricing-cofounder-cta"
                className="w-full"
              />
            </Card>
          </div>

          <div className="text-center">
            <Link to="/pricing" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
              View detailed pricing <ArrowRight className="inline ml-1" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Articles Section - Dark */}
      <section id="articles" className="bg-slate-800 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">From the Blog</h2>
          <p className="text-[#c7d4e4] text-center mb-12 max-w-2xl mx-auto">
            Insights on founder-investor alignment, early-stage partnerships, and building meaningful connections.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6" data-testid="homepage-articles-grid">
            {/* 2 Newest Articles */}
            {getHomepageArticles().slice(0, 2).map((article) => (
              <Card 
                key={article.id}
                className="bg-slate-700/50 border-slate-600 p-6 hover:border-blue-500 transition-colors group"
                data-testid={`homepage-article-${article.id}`}
              >
                <div className="text-blue-400 text-xs font-medium mb-3 uppercase">{article.category}</div>
                <h3 className="text-[#c7d4e4] font-semibold mb-3 group-hover:text-blue-400 transition-colors">
                  {article.title}
                </h3>
                <p className="text-[#a3b8d1] text-sm mb-4 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-[#a3b8d1] mb-4">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {article.date}
                  </span>
                  <span>{article.readTime}</span>
                </div>
                <Link 
                  to={`/blog/${article.id}`}
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  data-testid={`homepage-read-article-${article.id}`}
                >
                  Read full article
                  <ArrowRight size={14} />
                </Link>
              </Card>
            ))}

            {/* "More Articles" Card */}
            <Link 
              to="/blog"
              className="block"
              data-testid="more-articles-card"
            >
              <Card className="bg-slate-700/30 border-slate-600 border-dashed p-6 h-full flex flex-col items-center justify-center text-center hover:border-blue-500 hover:bg-slate-700/50 transition-all group min-h-[200px]">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/30 transition-colors">
                  <ArrowRight className="text-blue-400" size={24} />
                </div>
                <h3 className="text-[#c7d4e4] font-semibold mb-2 group-hover:text-blue-400 transition-colors">
                  More articles
                </h3>
                <span className="text-blue-400 text-sm font-medium">
                  View all
                </span>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Waitlist Section - Light */}
      <section id="waitlist-section" className="bg-slate-100 py-20">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">Request Early Access</h2>
            <p className="text-[#334d6e]">Join the waitlist and be first to know when we open private access.</p>
          </div>

          <WaitlistForm initialRole={waitlistRole} />
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-full shadow-xl transition-all duration-300 text-sm font-medium"
          aria-label="Back to top"
          data-testid="back-to-top-btn"
        >
          <ChevronUp size={18} />
          <span>Back to top</span>
        </button>
      )}

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setExpandedImage(null)}
          data-testid="expanded-image-modal"
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <button
              className="absolute -top-12 right-0 text-white hover:text-blue-400 transition-colors flex items-center gap-2"
              onClick={() => setExpandedImage(null)}
            >
              <span className="text-sm">Close</span>
              <X size={24} />
            </button>
            <img 
              src={expandedImage.src}
              alt={expandedImage.alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
